import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { type ProposalItem } from "@/lib/api";
import { formatDateTime, timeAgo } from "@/lib/format";

import { ApproveDialog, RejectDialog } from "./decision-dialogs";
import { DiffView } from "./diff-view";

interface ProposalCardProps {
  proposal: ProposalItem;
  onDecided: (result: ProposalItem) => void;
}

export function ProposalCard({ proposal, onDecided }: ProposalCardProps) {
  const [showDiff, setShowDiff] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-foreground text-lg font-semibold">{proposal.title}</h3>
            <p className="text-muted font-mono text-xs break-all">{proposal.path}</p>
          </div>
          <span className="bg-accent-light text-primary rounded-full px-2.5 py-0.5 text-xs font-medium">
            {proposal.tier}
          </span>
        </div>

        <div className="text-sm">
          <p>
            <span className="font-medium">{proposal.proposer_name ?? proposal.proposer_email}</span>{" "}
            <span className="text-muted" title={formatDateTime(proposal.created_at)}>
              proposed this {timeAgo(proposal.created_at)}
            </span>
          </p>
          <p className="text-foreground mt-2 whitespace-pre-wrap">{proposal.reason}</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setShowDiff((v) => !v)}
            className="text-accent inline-flex items-center gap-1 text-sm font-medium hover:underline"
            aria-expanded={showDiff}
          >
            {showDiff ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {showDiff ? "Hide changes" : "Show changes"}
          </button>
          {proposal.pr_url && (
            <a
              href={proposal.pr_url}
              target="_blank"
              rel="noreferrer"
              className="text-muted hover:text-foreground inline-flex items-center gap-1 text-sm"
            >
              Pull request{proposal.pr_number ? ` #${proposal.pr_number}` : ""}
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setRejectOpen(true)}>
              Reject
            </Button>
            <Button size="sm" onClick={() => setApproveOpen(true)}>
              Approve
            </Button>
          </div>
        </div>

        {showDiff && <DiffView proposalId={proposal.id} />}
      </CardContent>

      <ApproveDialog
        proposal={proposal}
        open={approveOpen}
        onOpenChange={setApproveOpen}
        onDecided={onDecided}
      />
      <RejectDialog
        proposal={proposal}
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        onDecided={onDecided}
      />
    </Card>
  );
}
